import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    Autocomplete, Box, Button, Dialog, DialogActions, DialogContent,
    Grid, IconButton, Paper, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
    createDealerAction,
    clearCreateDealerAction,
    getAllDealersAction,
    getAllDistrictsAction,
    getAllMandalsAction,
    getAllVillagesAction,
} from "../store/actions";

/* ── Search-or-Create input for Mandal / Village ── */
const RefLocationInput = ({ label, value, selectedId, suggestions, onTextChange, onSelect }) => {
    const [focused, setFocused] = useState(false);

    const normalize = (str) => (str || "").toLowerCase().replace(/\s+/g, "");
    const filtered = useMemo(() => {
        if (!value?.trim()) return [];
        const inputNorm = normalize(value);
        return suggestions.filter((s) => normalize(s.label).includes(inputNorm));
    }, [value, suggestions]);

    const showSuggestions = focused && value?.trim().length > 0 && filtered.length > 0;
    const isNew = value?.trim().length > 0 && !selectedId;

    let displayHelper = "";
    let helperColor;
    if (selectedId) {
        displayHelper = `✓ Existing ${label.toLowerCase()} selected`;
        helperColor = "success.main";
    } else if (isNew) {
        displayHelper = `New ${label.toLowerCase()} will be created`;
        helperColor = "info.main";
    }

    return (
        <Box sx={{ position: "relative" }}>
            <TextField
                fullWidth
                size="small"
                value={value}
                onChange={(e) => onTextChange(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 200)}
                placeholder={`Search or create ${label.toLowerCase()}`}
                helperText={displayHelper}
                FormHelperTextProps={{ sx: { color: helperColor } }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5, backgroundColor: "#FAFAFA" } }}
            />
            {showSuggestions && (
                <Paper elevation={4} sx={{ position: "absolute", zIndex: 1300, width: "100%", maxHeight: 180, overflow: "auto", mt: 0.5, borderRadius: 1 }}>
                    {filtered.map((item) => (
                        <Box
                            key={item.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { onSelect(item); setFocused(false); }}
                            sx={{ py: 0.75, px: 1.5, cursor: "pointer", "&:hover": { bgcolor: "#F3F6F9" } }}
                        >
                            <Typography variant="body2">{item.label}</Typography>
                        </Box>
                    ))}
                </Paper>
            )}
        </Box>
    );
};

/* ── Create Dealer Dialog ──
 * Reusable "+ Add Dealer" popup, same behaviour as the dealer-creation
 * dialog on the Farmer Commitment screen. Drop it next to any dealer
 * Autocomplete and call onCreated(newDealerId) to auto-select the result.
 */
const CreateDealerDialog = ({ open, onClose, onCreated }) => {
    const dispatch = useDispatch();

    const { successData: districts } = useSelector((s) => s.getAllDistrictsReducer || {});
    const { successData: mandals } = useSelector((s) => s.getAllMandalsReducer || {});
    const { successData: villages } = useSelector((s) => s.getAllVillagesReducer || {});
    const { successData: createDealerSuccess, error: createDealerError } = useSelector((s) => s.createDealerReducer || {});

    const districtOptions = useMemo(() => districts?.data?.districts || districts?.districts || [], [districts]);
    const mandalOptions = useMemo(() => mandals?.data?.mandals || mandals?.mandals || [], [mandals]);
    const villageOptions = useMemo(() => villages?.data?.villages || villages?.villages || [], [villages]);

    const [dealerForm, setDealerForm] = useState({
        fullName: "", districtId: "", districtName: "",
        mandalId: "", mandalName: "", villageId: "", villageName: "",
    });

    useEffect(() => {
        if (!open) return;
        if (!districtOptions.length) dispatch(getAllDistrictsAction());
        if (!mandalOptions.length) dispatch(getAllMandalsAction());
        if (!villageOptions.length) dispatch(getAllVillagesAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleFormChange = (field, value) => {
        setDealerForm((prev) => ({ ...prev, [field]: value }));
    };

    const resetAndClose = () => {
        setDealerForm({ fullName: "", districtId: "", districtName: "", mandalId: "", mandalName: "", villageId: "", villageName: "" });
        dispatch(clearCreateDealerAction());
        onClose();
    };

    const handleCreate = () => {
        if (!(dealerForm.fullName && (dealerForm.districtId || dealerForm.districtName) && (dealerForm.mandalId || dealerForm.mandalName) && (dealerForm.villageId || dealerForm.villageName))) {
            return;
        }
        dispatch(createDealerAction({
            name: dealerForm.fullName,
            districtId: Number(dealerForm.districtId),
            mandalId: dealerForm.mandalId ? Number(dealerForm.mandalId) : dealerForm.mandalName,
            villageId: dealerForm.villageId ? Number(dealerForm.villageId) : dealerForm.villageName,
        }));
    };

    useEffect(() => {
        if (!open) return;
        if (createDealerSuccess) {
            toast.success("Dealer created successfully");
            const newDealerId = createDealerSuccess.data?.dealerId || createDealerSuccess.dealerId;
            dispatch(getAllDealersAction());
            dispatch(clearCreateDealerAction());
            setDealerForm({ fullName: "", districtId: "", districtName: "", mandalId: "", mandalName: "", villageId: "", villageName: "" });
            onClose();
            if (newDealerId) onCreated?.(newDealerId);
        } else if (createDealerError) {
            toast.error("Failed to create dealer");
            dispatch(clearCreateDealerAction());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createDealerSuccess, createDealerError]);

    return (
        <Dialog open={open} onClose={resetAndClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 3, pb: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Create Dealer</Typography>
                <IconButton onClick={resetAndClose} aria-label="close dialog">
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ pt: 2, pb: 1 }}>
                <Grid container spacing={2}>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 600, mb: 0.5 }}>Dealer Name</Typography>
                        <TextField
                            size="small" fullWidth value={dealerForm.fullName}
                            onChange={(e) => handleFormChange("fullName", e.target.value)}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 600, mb: 0.5 }}>District</Typography>
                        <Autocomplete
                            options={districtOptions}
                            value={districtOptions.find((d) => String(d.districtId) === String(dealerForm.districtId)) || null}
                            onChange={(_, newValue) => {
                                handleFormChange("districtId", newValue ? String(newValue.districtId) : "");
                                handleFormChange("districtName", newValue ? newValue.name : "");
                                handleFormChange("mandalId", ""); handleFormChange("mandalName", "");
                                handleFormChange("villageId", ""); handleFormChange("villageName", "");
                            }}
                            getOptionLabel={(d) => (d ? d.name : "")}
                            isOptionEqualToValue={(option, value) => String(option.districtId) === String(value.districtId)}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Select district" size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.5 } }} />
                            )}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 600, mb: 0.5 }}>Mandal</Typography>
                        <RefLocationInput
                            label="Mandal"
                            value={mandalOptions.find((m) => String(m.mandalId) === String(dealerForm.mandalId))?.name || dealerForm.mandalName || ""}
                            selectedId={dealerForm.mandalId}
                            suggestions={(dealerForm.districtId
                                ? mandalOptions.filter((m) => String(m.districtId) === String(dealerForm.districtId))
                                : mandalOptions
                            ).map((m) => ({ id: String(m.mandalId), label: m.name }))}
                            onTextChange={(text) => {
                                handleFormChange("mandalName", text);
                                const normalize = (str) => (str || "").toLowerCase().replace(/\s+/g, "");
                                const inputNorm = normalize(text);
                                const mandalSuggs = dealerForm.districtId
                                    ? mandalOptions.filter((m) => String(m.districtId) === String(dealerForm.districtId))
                                    : mandalOptions;
                                const mandalMatch = mandalSuggs.find((m) => normalize(m.name) === inputNorm);
                                handleFormChange("mandalId", mandalMatch ? String(mandalMatch.mandalId) : "");
                                handleFormChange("villageId", ""); handleFormChange("villageName", "");
                            }}
                            onSelect={(item) => {
                                handleFormChange("mandalId", item.id);
                                handleFormChange("mandalName", item.label);
                                handleFormChange("villageId", ""); handleFormChange("villageName", "");
                            }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 600, mb: 0.5 }}>Village</Typography>
                        <RefLocationInput
                            label="Village"
                            value={villageOptions.find((v) => String(v.villageId) === String(dealerForm.villageId))?.name || dealerForm.villageName || ""}
                            selectedId={dealerForm.villageId}
                            suggestions={(dealerForm.mandalId
                                ? villageOptions.filter((v) => String(v.mandalId) === String(dealerForm.mandalId))
                                : villageOptions
                            ).map((v) => ({ id: String(v.villageId), label: v.name }))}
                            onTextChange={(text) => {
                                handleFormChange("villageName", text);
                                const normalize = (str) => (str || "").toLowerCase().replace(/\s+/g, "");
                                const inputNorm = normalize(text);
                                const villageSuggs = dealerForm.mandalId
                                    ? villageOptions.filter((v) => String(v.mandalId) === String(dealerForm.mandalId))
                                    : villageOptions;
                                const villageMatch = villageSuggs.find((v) => normalize(v.name) === inputNorm);
                                handleFormChange("villageId", villageMatch ? String(villageMatch.villageId) : "");
                            }}
                            onSelect={(item) => {
                                handleFormChange("villageId", item.id);
                                handleFormChange("villageName", item.label);
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                    variant="contained"
                    sx={{ background: "#4F46E5", borderRadius: 3, fontWeight: 600, textTransform: "none", px: 3, py: 1.2 }}
                    onClick={handleCreate}
                    disabled={!(
                        dealerForm.fullName &&
                        (dealerForm.districtId || dealerForm.districtName) &&
                        (dealerForm.mandalId || dealerForm.mandalName) &&
                        (dealerForm.villageId || dealerForm.villageName)
                    )}
                >
                    Create Dealer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

CreateDealerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreated: PropTypes.func,
};

export default CreateDealerDialog;
